import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@sundkortid/ui';
import type { PassType } from '@sundkortid/shared';
import { PASS_LABELS_IS } from '@sundkortid/shared';
import { api, ApiRequestError } from '../../api/client';

const PASS_DESCRIPTIONS_FALLBACK: Record<PassType, string> = {
  single: 'Eitt skipti í þátttökusundlaug',
  ten_pack: '10 skipti í þátttökusundlaugum',
  monthly: 'Ótakmarkað aðgengi í 30 daga',
  annual: 'Ótakmarkað aðgengi í 365 daga',
};

const PASS_ICONS: Record<PassType, React.ReactNode> = {
  single: (
    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
    </svg>
  ),
  ten_pack: (
    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
    </svg>
  ),
  monthly: (
    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
    </svg>
  ),
  annual: (
    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
    </svg>
  ),
};

interface PassPricing {
  passType: PassType;
  priceIsk: number;
  description: string | null;
}

export function BuyPassPage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<PassPricing[]>([]);
  const [fetchingPricing, setFetchingPricing] = useState(true);
  const [loading, setLoading] = useState<PassType | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<PassPricing[]>('/passes/pricing')
      .then(setPricing)
      .catch(() => setError('Ekki tókst að sækja verð'))
      .finally(() => setFetchingPricing(false));
  }, []);

  const handlePurchase = async (passType: PassType) => {
    try {
      setLoading(passType);
      setError(null);
      await api.post('/passes', {
        passType,
        idempotencyKey: crypto.randomUUID(),
      });
      navigate('/passes');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Villa kom upp við kaup');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-primary-400 mb-2">
          Verðskrá
        </p>
        <h1 className="text-3xl font-title font-bold text-primary-900 mb-2">Kauptu sundkort</h1>
        <p className="text-gray-400">Veldu kort sem hentar þér</p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {fetchingPricing && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 h-64 animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!fetchingPricing && pricing.length === 0 && !error && (
        <p className="text-gray-400">Engin kort eru í boði eins og er.</p>
      )}

      {/* Pricing grid */}
      {!fetchingPricing && pricing.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 stagger-children">
          {pricing.map((opt) => {
            const isPopular = opt.passType === 'ten_pack';
            return (
              <div
                key={opt.passType}
                className={`relative flex flex-col rounded-2xl border-2 bg-white p-6 transition-all duration-200 hover:shadow-lg ${
                  isPopular
                    ? 'border-primary-300 shadow-md'
                    : 'border-gray-100 hover:border-primary-200'
                }`}
              >
                {/* Popular badge */}
                {isPopular && (
                  <div className="absolute -top-3 left-6 bg-gradient-primary text-white text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                    Vinsælast
                  </div>
                )}

                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mb-5">
                  {PASS_ICONS[opt.passType]}
                </div>

                {/* Name + description */}
                <h3 className="text-lg font-title font-bold text-primary-900">
                  {PASS_LABELS_IS[opt.passType]}
                </h3>
                <p className="text-sm text-gray-400 mt-1 mb-6 flex-1">
                  {opt.description ?? PASS_DESCRIPTIONS_FALLBACK[opt.passType]}
                </p>

                {/* Price */}
                <div className="mb-5">
                  <span className="text-3xl font-bold text-primary-900">
                    {opt.priceIsk.toLocaleString('is-IS')}
                  </span>
                  <span className="text-sm text-gray-400 ml-1">kr.</span>
                </div>

                {/* CTA */}
                <Button
                  onClick={() => handlePurchase(opt.passType)}
                  disabled={loading !== null}
                  loading={loading === opt.passType}
                  className="w-full"
                >
                  Kaupa
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
